import axios from 'axios';

// Point the app at a local API with REACT_APP_API_BASE_URL (see
// .env.development.local). Falls back to the deployed server, so a build with no
// env var set behaves exactly as before. CRA inlines this at build time, so a
// change to the env file needs a dev-server restart.
//
// NOTE: components/pages/const.js carries a SECOND base URL used by login and
// several pages. It reads the same variable -- both must stay in step.
const API_URL = process.env.REACT_APP_API_BASE_URL || 'https://ipoultry.digiterrain.live/api/v1';
const apiInstance = axios.create({
  baseURL: API_URL,
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
     if (error) {
       prom.reject(error);
     } else {
       prom.resolve(token);
     }
  });

  failedQueue = [];
};
// Add an interceptor to inject the Authorization header
apiInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  config.headers.Authorization = `Bearer ${token}`;
  return config;
});

apiInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if ((error.response.status === 401 && !originalRequest._retry) || error.response.status === 403 && !originalRequest._retry) {
      alert(isRefreshing)
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = 'Bearer ' + token;
          return apiInstance(originalRequest);
        }).catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;
      const refreshToken = localStorage.getItem('refreshToken');

      try {
        const res = await axios.post(API_URL + '/refresh_token', { refreshToken });
        if (res.status === 201) {
          localStorage.setItem('token', res.data.accessToken);
          apiInstance.defaults.headers.common['Authorization'] = 'Bearer ' + res.data.accessToken;
          processQueue(null, res.data.accessToken);
          return apiInstance(originalRequest);
        } else {
          throw new Error();
        }
      } catch (err) {
        processQueue(err, null);
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        window.location.href = "/login"; // Redirect to login page
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);
// const handleRequest = async (url, data) => {
//   try {
//     const response = data 
//       ? await apiInstance.post(url, data)
//       : await apiInstance.get(url);
//     return response.data;
//   } catch (error) {
//     // Handle error more gracefully and log only in development if needed
//     throw error;
//   }
// };
const handleRequest = async (url, data, method, requireAuth = true) => {
  try {
    let response;
    const headers = {};

    // Conditionally add the auth token to the headers
    if (requireAuth) {
      const token = localStorage.getItem('token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      } else {
        throw new Error('Authorization token is missing');
      }
    }
    switch (method) {
      case 'POST':
        response = await apiInstance.post(url, data, { headers });
        break;
      case 'PUT':
        response = await apiInstance.put(url, data, { headers });
        break;
      case 'DELETE':
        response = await apiInstance.delete(url, { headers });
        break;
      case 'POSTDOWNLOAD':
        response = await apiInstance.post(url, data, { headers, responseType: 'blob' });
        break;
      case 'GET':
      default:
        response = await apiInstance.get(url, { headers });
        break;
    }

    return response.data;
  } catch (error) {
    throw error;
  }
};

// `config` is merged into the axios request, so a caller can pass an
// AbortController signal for a request it may need to cancel (a modal closed
// before its fetch returned). Optional -- every existing caller passes two
// arguments and is unaffected.
const getFromApi = async (endpoint, requireAuth, config = {}) => {
  try {
    const headers = {};
    if (requireAuth) {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Unauthorized');
      headers.authorization = `Bearer ${token}`;
    }
    const response = await axios.get(`${API_URL}${endpoint}`, { headers, ...config });
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 401) {
      // Redirect to login if 401 is received
      window.location.href = "/login";  // Redirect to login page
    }
    // An aborted request has no `.response` and is not a failure worth logging.
    if (error && (error.code === 'ERR_CANCELED' || error.name === 'CanceledError' || error.name === 'AbortError')) {
      throw error;
    }
    console.error(error);
    throw error;
  }
};


export const getSignalData = (deviceID) => getFromApi(`/misc/signalstrength/${deviceID}`, true);
export const getDashBoardValues = (farmID, date) => getFromApi(`/dashboard/${farmID}/${date}`, true);
// Fetched lazily when the Feed Analysis modal opens, deliberately NOT folded
// into the polled dashboard payload -- it carries a per-day series most
// sessions never look at.
export const getFeedAnalysis = (farmID, signal) =>
  getFromApi(`/feedAnalysis/${farmID}`, true, signal ? { signal } : {});

export const addbarn = (data) => handleRequest(`/farmMaster/`, data, 'POST', true);
export const addBatch = (data) => handleRequest(`/batchMaster/`, data, 'POST', true);
export const farmAction = (data) => handleRequest(`/farmaction/`, data, 'POST', true);
export const downloadDopReport = (data) => handleRequest(`/download/dopreport/`, data, 'POSTDOWNLOAD', true);

export const updateFanStatus = (data) => handleRequest(`/misc/updateSwitch/`, data, 'PUT', true);
// i18n Phase 4 — persists the signed-in user's UI language server-side.
export const updateUserLanguage = (language) => handleRequest(`/userLanguage`, { language }, 'PUT', true);
export const addDailyParams = (data) => handleRequest(`/addweight/`, data, 'POST', true);

export const addUser = (data) => handleRequest(`/addUser/`, data, 'POST', true);
export const addCredentials = (data) => handleRequest(`/credentials/add`, data, 'PUT', false);
// Reports on a password link WITHOUT consuming it: whether it is still usable,
// whether it is a first-time invite or a reset, and which account it belongs to.
// The token is opaque, so the browser cannot work any of that out for itself --
// and without asking, someone would type a password twice into a dead link before
// finding out. Unauthenticated by definition: the holder of the link has no
// session, and may be about to recover the only thing that would give them one.
export const validateCredentialToken = (token) =>
  getFromApi(`/credentials/validate/${encodeURIComponent(token)}`, false);

export const udpatePassword = (data) => handleRequest(`/forgotpassword/update`, data, 'POST',false );

export const callibrations = (data) => handleRequest(`/callibrations/`, data, 'POST', true);
export const controlsystem = (data) => handleRequest(`/controlsystem/add`, data, 'POST', true);

export const updateCSStatus = (data) => handleRequest(`/controlsystem/updateStatus`, data, 'POST', true);
export const addIntegrator = (data) => handleRequest(`/integrators/add`, data, 'POST', true);
export const modifyIntegrator = (data) => handleRequest(`/integrators/modify`, data, 'POST', true);
export const batchComparison_values = (data) => handleRequest(`/batchSummary/batchComparison`, data, 'POST', true);
export const growthComparison_values = (data) => handleRequest(`/batchSummary/growthComparison`, data, 'POST', true);

export const getListofFarmers_admin = () => getFromApi(`/misc/listoffarmers/`, true);
export const getListofFarms = (input) => getFromApi(`/misc/listoffarms/${input}`, true);
export const getComparisonFarms = () => getFromApi(`/misc/farmsForComparison/`, true);


export const getListofFarms_active = (input) => getFromApi(`/misc/listoffarmsDashboard/${input}`, true);
export const getListofFarms_active_admin = (input) => getFromApi(`/misc/listoffarmsDashboard_admin/${input}`, true);

export const getListofTemplates = () => getFromApi(`/misc/listoftemplates`, true);
export const getListofAssignedDevices = (input) => getFromApi(`/assigndevices/listofavailabledevices/${input}/true`, true);

export const getListofBarns = () => getFromApi(`https://ipoultry.digiterrain.live/listofbarns`, true);

export const getFarmMasterView = () => getFromApi(`/farmMaster/view`, true);
export const getassignedusers = (input) => getFromApi(`/misc/assignedusers/${input}`, true);

export const getTemplatesPhases = (input) => getFromApi(`/templates/phases/${input}`, true);
export const getTemplates = () => getFromApi(`/templates`, true);
export const getFansByFarm = (input) => getFromApi(`/misc/faninfo/${input}`, true);
export const getIdealComparison = (input) => getFromApi(`/analytics/idealscomparison/${input}`, true);
export const getBreeds = () => getFromApi(`/analytics/breeds/`, true);
export const getIdealscomparisonbygender = (input) => getFromApi(`/analytics/idealscomparisonbygender/${input}`, true);

export const getListofBatches = (farmID, type) => getFromApi(`/misc/listofbatches/${farmID}/${type}`, true);
export const getMortlalityData = (batchID, filter) => getFromApi(`/analytics/ml/mortality/${batchID}/${filter}`, true);
export const getBatchwiseMortality = (type, batchID) => getFromApi(`/analytics/mortality/${type}/${batchID}`, true);
export const getAllUsers = () => getFromApi(`/fetchAllUsers`, true);
// Enable/disable a login. Narrow by design -- it writes only `status`, unlike
// addUser's Update action which rewrites the whole user row. Admin only; the
// API also refuses to let you change your own status.
export const updateUserStatus = (id, status) =>
  handleRequest(`/users/${id}/status`, { status }, 'PUT', true);
// Re-issues a set-password invite and emails it. POST, not PUT: it mints a fresh
// token each time and is not idempotent. The server invalidates any outstanding
// invite for that user first, so the confirm dialog's "any earlier link will stop
// working" is literally true. Admin only; refused when the account already has a
// password, has no email on file, or is INACTIVE.
export const resendInvite = (id) =>
  handleRequest(`/users/${id}/invite`, {}, 'POST', true);
// The reset counterpart: mails a link that REPLACES a live password, for the
// farmer who has forgotten both their password and their user name. Its own path
// rather than a flag on the one above, because it is a materially more powerful
// capability. Admin only; refused for an account with no password yet (that is an
// invite), no email on file, INACTIVE, an Admin target, or your own row. Links
// last 24h against the invite's 72h.
export const sendPasswordReset = (id) =>
  handleRequest(`/users/${id}/reset-password`, {}, 'POST', true);
export const getImeiNumber = (batchID) => getFromApi(`/misc/imeinumber/${batchID}`, true);
export const getBatchOperationsData = (batchID) => getFromApi(`/batchOperations/${batchID}`, true);

export const getBatchReadings = (date, batchID) => getFromApi(`/readings/${date}/${batchID}`, true);

export const getThresholds = (batchID, phaseID) => getFromApi(`/minmax/${batchID}/${phaseID}`, true);
export const getBatchSummary = (batchID) => getFromApi(`/batchSummary/${batchID}/`, true);
export const getExpensesList = (batchID) => getFromApi(`/expenses/list/${batchID}/`, true);
export const getBatchWeights = (batchID) => getFromApi(`/closeBatch/getBatchWeights/${batchID}/`, true);
export const getExpensesStatus = (batchID) => getFromApi(`/expenses/status/${batchID}/`, true);
export const getDashboardBatchSummary = (farmID, date, batchID, phase) => getFromApi(`/dashboard/batchSummary/${farmID}/${date}/${batchID}/${phase}`, true);
//getHeatIndexDetails
export const getHeatIndexDetails = (data) => handleRequest(`/analytics/getHeatIndexDetails`, data, 'POST', true);

export const getDailyOperationsStatus = (farmID, date) => getFromApi(`/addweight/status/${farmID}/${date}`, true);

// Bulk per-day entry status for an entire batch (one object per day, same
// _status field names as getDailyOperationsStatus). Used by the chat widget's
// day-grid completeness rings.
export const getBatchDayStatuses = (batchID) => getFromApi(`/addweight/statusByBatch/${batchID}`, true);

export const getGrowthCurve = (batchID) => getFromApi(`/analytics/growthcurve/${batchID}`, true);
export const getGrowthCurveData = (batchID) => getFromApi(`/analytics/growthcurvedata/${batchID}`, true);

export const getConnectionsList = () => getFromApi(`/misc/listofdeviceswithstatus`, true);
export const getEmailValidation = (input) => getFromApi(`/forgotpassword/${input}`, false);
export const getUserOptions = (input) => getFromApi(`/misc/userOptions/${input}`, true);


export const getDevices = (input) => getFromApi(`/misc/deviceListByFarmer/${input}`, true);
export const getUserProfile = () => getFromApi(`/misc/userProfile/`, true);
export const getVideos = () => getFromApi(`/misc/videosList/`, true);

export const getControlSystemData = (farmID, fanID) => getFromApi(`/controlsystem/data/${farmID}/${fanID}`, true);

export const getIntegratorsList = (input) => getFromApi(`/integrators/list/${input}`, true);
export const getBatchesByTimeInterval = (startDate, endDate, farmID) => getFromApi(`/misc/batchesByTimeInterval/${startDate}/${endDate}/${farmID}`, true);

export const updateConstrolSystemStatus = (data) => handleRequest(`/misc/updateControlSystem/`, data, 'POST', true);
